<template>
  <div class="page-wrapper">
    <!-- Modern Header -->
    <header class="header">
      <div class="header-container">
        <router-link to="/" class="logo-link">
          <img src="@/assets/logo.png" alt="Nexthome4U Logo" class="logo" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div class="otp-card">
        <div class="card-header">
          <h1 class="title">Verify Your Email</h1>
          <p class="subtitle">Enter the verification code sent to your email address</p>
          <div class="email-display">{{ email }}</div>
        </div>

        <form class="form" @submit.prevent="submitForm">
          <div class="otp-section">
            <div class="otp-inputs">
              <input 
                type="text" 
                maxlength="1" 
                v-model="emailOtp1" 
                @input="focusNext($event, 'emailOtp2')"
                @keydown="handleBackspace($event, 'emailOtp1', null)" 
                ref="emailOtp1" 
                class="otp-input" 
                pattern="[0-9]"
              />
              <input 
                type="text" 
                maxlength="1" 
                v-model="emailOtp2" 
                @input="focusNext($event, 'emailOtp3')"
                @keydown="handleBackspace($event, 'emailOtp2', 'emailOtp1')" 
                ref="emailOtp2" 
                class="otp-input" 
                pattern="[0-9]"
              />
              <input 
                type="text" 
                maxlength="1" 
                v-model="emailOtp3" 
                @input="focusNext($event, 'emailOtp4')"
                @keydown="handleBackspace($event, 'emailOtp3', 'emailOtp2')" 
                ref="emailOtp3" 
                class="otp-input" 
                pattern="[0-9]"
              />
              <input 
                type="text" 
                maxlength="1" 
                v-model="emailOtp4"
                @keydown="handleBackspace($event, 'emailOtp4', 'emailOtp3')" 
                ref="emailOtp4" 
                class="otp-input" 
                pattern="[0-9]"
              />
            </div>
          </div>

          <button type="submit" class="submit-button" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Verify Code</span>
            <div v-if="isSubmitting" class="loading-spinner">
              <div class="spinner"></div>
              <span>Verifying...</span>
            </div>
          </button>

          <div class="resend-section">
            <p class="resend-text">Didn't receive the code?</p>
            <button type="button" class="resend-button" @click="resendCode" :disabled="resendTimer > 0">
              <span v-if="resendTimer === 0">Resend Code</span>
              <span v-else class="resend-timer">
                Resend in {{ resendTimer }}s
              </span>
            </button>
          </div>
        </form>
      </div>
    </main>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { waitForToken } from '@/utilities/tokenHelper';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
  name: 'OtpVerificationPage',
  data() {
    return {
      email: '', // You may pass this as a prop or via query param
      emailOtp1: '',
      emailOtp2: '',
      emailOtp3: '',
      emailOtp4: '',
      isSubmitting: false,
      resendTimer: 0,
      resendInterval: null,
      toasts: []
    };
  },
  methods: {
    // Toast notification methods
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },

    // Timer methods
    startResendTimer() {
      this.resendTimer = 30;
      this.resendInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendInterval) {
        clearInterval(this.resendInterval);
        this.resendInterval = null;
      }
      this.resendTimer = 0;
    },

    async submitForm() {
      const otp = this.emailOtp1 + this.emailOtp2 + this.emailOtp3 + this.emailOtp4;
      if (otp.length !== 4 || !/^\d{4}$/.test(otp)) {
        this.showToast('Please enter a valid 4-digit OTP', 'warning');
        return;
      }

      this.isSubmitting = true;

      try {
        await waitForToken() // waits for token
        const response = await apiService.verifyEmailOtp(this.email, otp);

        if (response.data && response.data.success) {
          this.$router.push({ path: '/reset/password', query: { email: this.email, otp: otp } });
        } else {
          // For HTTP 400 errors, always use the reason field from API response
          if (response.data.reason) {
            this.showToast(response.data.reason, 'error');
          } else if (response.data.message) {
            this.showToast(response.data.message, 'error');
          }
          // If no reason/message provided for 400 error, don't show toast (no generic messages)
        }
      } catch (error) {
        console.error('OTP Verification Error:', error);
        // Skip error handling for "no data" cases (status 400 with errorcode 2003)
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        
        let message = null;
        if (error?.response?.data) {
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            }
            // If no reason/message provided for 400 error, don't show toast (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            }
          }
        }
        
        // Only show toast if we have a message (genuine errors only)
        if (message) {
          this.showToast(message, 'error');
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async resendCode() {
      if (!this.email) {
        this.showToast('Email address is required to resend OTP', 'warning');
        return;
      }

      try {
        // Get device token from localStorage
        const deviceToken = localStorage.getItem('DEVICE_REG_TOKEN');
        if (!deviceToken) {
          this.showToast('Device token not found. Please try again.', 'error');
          return;
        }

        // Call resend OTP API
        const response = await apiService.sendEmailOtp(this.email);

        if (response.data && response.data.success) {
          // Clear OTP fields
          this.emailOtp1 = '';
          this.emailOtp2 = '';
          this.emailOtp3 = '';
          this.emailOtp4 = '';
          
          // Focus first input
          this.$nextTick(() => {
            this.$refs.emailOtp1.focus();
          });
          
          // Start the 30-second timer
          this.startResendTimer();
        } else {
          // For HTTP 400 errors, always use the reason field from API response
          if (response.data.reason) {
            this.showToast(response.data.reason, 'error');
          } else if (response.data.message) {
            this.showToast(response.data.message, 'error');
          }
          // If no reason/message provided for 400 error, don't show toast (no generic messages)
        }
      } catch (error) {
        console.error('Resend OTP Error:', error);
        // Skip error handling for "no data" cases (status 400 with errorcode 2003)
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        
        let message = null;
        if (error?.response?.data) {
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            }
            // If no reason/message provided for 400 error, don't show toast (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            if (errorData.reason) {
              message = errorData.reason;
            } else if (errorData.message) {
              message = errorData.message;
            } else {
              // Fallback messages for specific status codes only if no reason/message
              if (status === 401) {
                message = 'Session expired. Please try again.';
              } else if (status === 429) {
                message = 'Too many requests. Please wait a moment before trying again.';
              }
            }
          }
        }
        
        // Only show toast if we have a message (genuine errors only)
        if (message) {
          this.showToast(message, 'error');
        }
      }
    },

    focusNext(event, nextField) {
      if (event.target.value && this.$refs[nextField]) {
        this.$refs[nextField].focus();
      }
    },

    handleBackspace(event, currentField, previousField) {
      if (event.key === 'Backspace' && !event.target.value && previousField && this.$refs[previousField]) {
        this.$refs[previousField].focus();
      }
    }
  },
  mounted() {
    this.$refs.emailOtp1.focus(); // Focus on the first OTP field by default
    this.email = this.$route.query.email;
    console.log('Received email:', this.email);
  },

  beforeUnmount() {
    // Clean up timer when component is destroyed
    this.clearResendTimer();
  }
};
</script>


<style scoped>

* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background: #fcfbfb;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}


.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 40px;
  width: auto;
  max-width: 150px;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: calc(100vh - 80px);
}

.otp-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 32px 64px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 3rem;
  width: 100%;
  max-width: 420px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.email-display {
  background: rgba(91, 46, 142, 0.1);
  color: #5b2e8e;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px solid rgba(91, 46, 142, 0.2);
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.otp-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.otp-input {
  width: 4rem;
  height: 4rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
  transform: scale(1.05);
}

.otp-input:hover {
  border-color: #d1d5db;
}

.submit-button {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.4);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.4);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.resend-section {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.resend-text {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.resend-button {
  background: none;
  border: none;
  color: #5b2e8e;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.resend-button:hover:not(:disabled) {
  background: rgba(91, 46, 142, 0.1);
  text-decoration: underline;
}

.resend-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.resend-timer {
  color: #6b7280;
  font-weight: 500;
}

/* Toast Notification Styles */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  min-width: 320px;
  pointer-events: auto;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
    padding: 14px 16px;
  }

  .toast-message {
    font-size: 14px;
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .main-content {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .otp-card {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    margin-top: 1rem;
  }

  .title {
    font-size: 1.75rem;
  }

  .otp-inputs {
    gap: 0.75rem;
  }

  .otp-input {
    width: 3.5rem;
    height: 3.5rem;
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .otp-card {
    padding: 1.5rem 1rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.925rem;
  }

  .otp-inputs {
    gap: 0.5rem;
  }

  .otp-input {
    width: 3rem;
    height: 3rem;
    font-size: 1.125rem;
  }
}
</style>